import {createStyles} from '@mantine/core';
import React, {useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Header1} from 'ui-library/Headers';
import Link from 'ui-library/Link';

export enum MONOMER_CONFIGURATION_PURPOSE {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  CUSTOM_NOTATION = 'CUSTOM_NOTATION',
  IDT_ALIASES = 'IDT_ALIASES',
}

const PURPOSE_TO_TITLE: {[key in MONOMER_CONFIGURATION_PURPOSE]: string} = {
  [MONOMER_CONFIGURATION_PURPOSE.CREATE]: 'Add monomers',
  [MONOMER_CONFIGURATION_PURPOSE.EDIT]: 'Edit monomers',
  [MONOMER_CONFIGURATION_PURPOSE.VIEW]: 'View monomers',
  [MONOMER_CONFIGURATION_PURPOSE.CUSTOM_NOTATION]: 'Custom notations',
  [MONOMER_CONFIGURATION_PURPOSE.IDT_ALIASES]: 'IDT aliases',
};

export interface SharedMonomerConfigurationPageContainerProps {
  purpose: MONOMER_CONFIGURATION_PURPOSE;
  libraryName: string;
  libraryUrl: string;
  children?: React.ReactNode;
  methodTitle?: string;
  changeMethodUrl?: string;
}

export default function SharedMonomerConfigurationPageContainer({
  children,
  methodTitle,
  purpose,
  libraryName,
  libraryUrl,
  changeMethodUrl,
}: SharedMonomerConfigurationPageContainerProps): JSX.Element {
  const {classes, cx} = useStyles();
  const title = PURPOSE_TO_TITLE[purpose];

  useEffect(() => {
    setWindowTitle(title);
  }, [title]);

  return (
    <div
      className={cx(
        classes.container,
        purpose !== MONOMER_CONFIGURATION_PURPOSE.EDIT && classes.marginBottom
      )}
      {...getTestProps({component: 'SharedMonomerConfigurationPageContainer', element: 'root'})}
    >
      <div className={classes.header}>
        <span
          className={classes.library}
          onClick={() => {
            navigateToURL(libraryUrl);
          }}
        >
          {libraryName} /
        </span>
        <div className={classes.subtitle}>{title}</div>
      </div>
      <div
        className={cx(
          classes.borderBottom,
          purpose !== MONOMER_CONFIGURATION_PURPOSE.EDIT && classes.marginBottom
        )}
      />
      <div className={classes.creationMethodSection}>
        {methodTitle && (
          <div className={classes.creationMethodHeader}>
            <Header1>{methodTitle}</Header1>
            {purpose === MONOMER_CONFIGURATION_PURPOSE.CREATE && changeMethodUrl && (
              <div className={classes.changeMethod}>
                <Link
                  onMouseDown={() => {
                    navigateToURL(changeMethodUrl);
                  }}
                >
                  Change method
                </Link>
              </div>
            )}
          </div>
        )}
        <div>{children}</div>
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    marginTop: 40,
  },
  marginBottom: {
    marginBottom: 16,
  },
  header: {
    marginLeft: 40,
  },
  library: {
    ...FONT_PROXIMA_13,
    color: theme.colors.gray[7],
    cursor: 'pointer',
    '&:hover': {
      color: theme.colors.blue[4],
    },
  },
  subtitle: {
    ...FONT_PROXIMA_17,
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    marginTop: 16,
  },
  creationMethodSection: {
    marginLeft: 40,
  },
  creationMethodHeader: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: 12,
    alignItems: 'center',
  },
  changeMethod: {
    paddingBottom: 2,
  },
}));
